/* EAPAHvZu — ONE Firebase-identity gate for the whole storefront.
 *
 * THE DEFECT CLASS THIS EXISTS TO CLOSE
 * -------------------------------------
 * This store runs two identity systems (see snippets/studio-signin-url.liquid).
 * The buy path's identity is Firebase (project `opsagents-studio-prod`),
 * established ONLY by /pages/signin. Shopify's `customer` object is the OTHER
 * system, and a Firebase sign-in never populates it.
 *
 * So any Liquid gate written as `{% if customer == blank %}` — or
 * `{% if shop.customer_accounts_enabled and customer == null %}` — while its
 * CTA links the Firebase door is an INFINITE LOOP by construction: the visitor
 * clicks sign-in, signs in successfully, comes back, and `customer` is STILL
 * blank, so the same prompt is still on screen. Card EAPAHvZu measured exactly
 * that on the composer. Two more sites shipped the same predicate:
 *   · sections/main-wishlist.liquid   (`customer == blank`)
 *   · snippets/cart-products.liquid   (`shop.customer_accounts_enabled and customer == null`)
 *
 * THE MECHANISM
 * -------------
 * Liquid cannot see the Firebase session, so the gate is resolved on the client
 * — the same shape snippets/studio-composer.liquid already uses, generalised so
 * a third site cannot re-invent it a third way:
 *
 *   <div data-studio-auth-gate="guest">…sign-in prompt…</div>   ← server-rendered VISIBLE
 *   <div data-studio-auth-gate="user" hidden>…signed-in state…</div>
 *
 * The attribute is `data-studio-auth-gate`, NOT `data-studio-gate`: card
 * am4jHQaI already claimed the shorter name on /pages/my-studio, where
 * assets/studio-my-studio-auth.js resolves it with a bare
 * `querySelector('[data-studio-gate]')` — a first-match-wins lookup that a
 * document-wide vocabulary would silently hijack the moment the two appeared
 * on one page. That surface is already correctly Firebase-gated (it is not a
 * member of this defect class); the distinct name keeps the two mechanisms
 * from ever competing for the same node.
 *
 * The guest branch is the SERVER-RENDERED DEFAULT, so a visitor with no JS, a
 * blocked gstatic, or a Firebase init failure still sees a working sign-in CTA
 * rather than a blank panel — the buy path is never silent. When Firebase
 * resolves to a signed-in user this module hides every `guest` branch and
 * reveals every `user` branch.
 *
 * It also stamps <html data-studio-auth="in|out"> and dispatches a
 * `studio:auth` CustomEvent ({ detail: { signedIn } }) so a page can render
 * Firebase-scoped content once, on one signal.
 *
 * Nothing here reads Shopify `customer`. That is the point.
 *
 * MORPH-PROOF (card SYKMBm5f, AC-1). The first version resolved the gate ONCE, on the
 * auth callback. Horizon's cart drawer and cart section re-insert their markup on every
 * cart mutation (section rendering), so a signed-in visitor who emptied the cart got a
 * FRESH server-rendered `guest` node — "Have an account? Log in" — that nothing revisited.
 * Measured: an inserted guest node stayed visible for a signed-in user. Now the resolved
 * state is re-applied to every gate node that ENTERS the document after it, via a
 * MutationObserver on the whole tree (cheap: it only looks at added element nodes), so
 * the gate is a property of the page, not of the nodes that happened to exist at auth
 * time. The `studio:auth` event is NOT re-dispatched on a morph — pages render
 * Firebase-scoped content on it once. Belt: snippets/cart-products.liquid also carries a
 * CSS rule keyed on the <html data-studio-auth> stamp, so the re-inserted guest prompt
 * never flashes before this observer runs.
 */
import { getApp, getApps } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-app.js';
import { getAuth, onAuthStateChanged } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-auth.js';

(function () {
  'use strict';

  var applied = false;
  var signedInState = null; // null until Firebase (or the safety net) has resolved
  var observer = null;

  function applyTo(node, signedIn) {
    var want = node.getAttribute('data-studio-auth-gate');
    // Unknown values are left alone rather than guessed at.
    if (want === 'guest') node.hidden = signedIn;
    else if (want === 'user') node.hidden = !signedIn;
  }

  function applyAll(root, signedIn) {
    var nodes = root.querySelectorAll('[data-studio-auth-gate]');
    for (var i = 0; i < nodes.length; i++) applyTo(nodes[i], signedIn);
  }

  // A morph inserted `node` (often a whole section wrapper): resolve it and every gate
  // node under it against the state the page already resolved to.
  function onInserted(node) {
    if (signedInState === null || !node || node.nodeType !== 1) return;
    if (typeof node.hasAttribute === 'function' && node.hasAttribute('data-studio-auth-gate')) {
      applyTo(node, signedInState);
    }
    if (typeof node.querySelectorAll === 'function') applyAll(node, signedInState);
  }

  function watchMorphs() {
    if (observer || typeof MutationObserver !== 'function') return;
    observer = new MutationObserver(function (records) {
      for (var r = 0; r < records.length; r++) {
        var added = records[r].addedNodes || [];
        for (var i = 0; i < added.length; i++) onInserted(added[i]);
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function apply(signedIn) {
    applied = true;
    signedInState = signedIn;
    document.documentElement.setAttribute('data-studio-auth', signedIn ? 'in' : 'out');
    applyAll(document, signedIn);
    watchMorphs();
    document.dispatchEvent(new CustomEvent('studio:auth', {
      detail: { signedIn: signedIn },
    }));
  }

  // firebase-auth.js (loaded earlier in layout/theme.liquid) must have called
  // initializeApp(). If it did not, the guest branch is already on screen and
  // correct — announce 'out' so pages waiting on `studio:auth` stop waiting.
  var auth = null;
  try {
    auth = getApps().length ? getAuth(getApp()) : null;
  } catch (e) {
    auth = null;
  }

  if (!auth) {
    apply(false);
    return;
  }

  onAuthStateChanged(auth, function (user) { apply(!!user); });

  // Safety net: if onAuthStateChanged never fires (network wall in front of
  // Firebase), fall back to the guest branch instead of leaving a page that
  // waits on `studio:auth` spinning forever.
  setTimeout(function () { if (!applied) apply(false); }, 8000);
})();
